import React, { useEffect } from 'react';
import { RefreshCw, ArrowRight, Lightbulb, Brain } from 'lucide-react';

const TemplateWizard = ({
  templateData,
  handleTemplateChange,
  templateTypes,
  usefulExpressions,
  resetTemplate,
  setActiveTab
}) => {
  // Initialize MathJax when component mounts
  useEffect(() => {
    if (window.MathJax) {
      window.MathJax.typesetPromise();
    }
  }, [templateData]);

  // Debug logging - disabled to prevent object rendering issues
  // useEffect(() => {
  //   console.log('TemplateWizard Debug:', {
  //     templateData,
  //     templateTypes,
  //     usefulExpressions,
  //     currentType: templateData?.type,
  //     currentExpressions: usefulExpressions?.[templateData?.type]
  //   });
  // }, [templateData, templateTypes, usefulExpressions]);

  // Calculate completion percentage for mathematics template
  const calculateCompletion = () => {
    const fields = ['problem', 'given', 'toProve', 'proof', 'conclusion'];
    const completedFields = fields.filter(field => 
      templateData[field] && templateData[field].trim() !== ''
    ).length;
    const formulasCompleted = templateData.formulas && templateData.formulas.length > 0 ? 1 : 0;
    return ((completedFields + formulasCompleted) / 6) * 100;
  };

  const getCompletedFieldsCount = () => {
    const fields = ['problem', 'given', 'toProve', 'proof', 'conclusion'];
    const completedFields = fields.filter(field => 
      templateData[field] && templateData[field].trim() !== ''
    ).length;
    const formulasCompleted = templateData.formulas && templateData.formulas.length > 0 ? 1 : 0;
    return completedFields + formulasCompleted;
  };

  const addFormula = () => {
    const newFormulas = [...(templateData.formulas || []), ''];
    handleTemplateChange('formulas', newFormulas);
  };

  const updateFormula = (index, value) => {
    const newFormulas = [...(templateData.formulas || [])];
    newFormulas[index] = value;
    handleTemplateChange('formulas', newFormulas);
  };

  const removeFormula = (index) => {
    const newFormulas = [...(templateData.formulas || [])];
    newFormulas.splice(index, 1);
    handleTemplateChange('formulas', newFormulas);
  };

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div 
        className="relative rounded-xl overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.9), rgba(147, 51, 234, 0.9)), url(https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <h2 className="text-3xl font-bold mb-3">模板精靈</h2>
          <p className="text-blue-100 text-lg">
            使用我們的引導式精靈創建完美的數學解題步驟
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">建立您的數學模板</h3>
            <p className="text-gray-600 mt-1">填寫以下詳細資訊以創建您的數學解題步驟</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={resetTemplate}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>重置</span>
            </button>
            <button
              onClick={() => setActiveTab('flashcards')}
              className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors group"
            >
              <Brain className="w-4 h-4" />
              <span>閃卡</span>
            </button>
            <button
              onClick={() => setActiveTab('generated')}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors group"
            >
              <span>生成結果</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
        
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Left Column - Basic Information */}
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-blue-900 mb-4">基本資訊</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">模板類型</label>
                  <select
                    value={templateData.type}
                    onChange={(e) => handleTemplateChange('type', e.target.value)}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  >
                    {templateTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">問題</label>
                  <textarea
                    value={templateData.problem || ''}
                    onChange={(e) => handleTemplateChange('problem', e.target.value)}
                    placeholder="描述要解決的數學問題"
                    rows="3"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-green-600 mb-2">已知條件</label>
                  <textarea
                    value={templateData.given || ''}
                    onChange={(e) => handleTemplateChange('given', e.target.value)}
                    placeholder="列出已知的條件和資訊"
                    rows="3"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-yellow-600 mb-2">求證/求解</label>
                  <textarea
                    value={templateData.toProve || ''}
                    onChange={(e) => handleTemplateChange('toProve', e.target.value)}
                    placeholder="說明要證明或求解的內容"
                    rows="2"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Solution Content */}
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-green-900 mb-4">解題內容</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-purple-600 mb-2">證明/解答過程</label>
                  <textarea
                    value={templateData.proof || ''}
                    onChange={(e) => handleTemplateChange('proof', e.target.value)}
                    placeholder="詳細的證明或解答步驟"
                    rows="8"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-orange-600 mb-2">結論</label>
                  <textarea
                    value={templateData.conclusion || ''}
                    onChange={(e) => handleTemplateChange('conclusion', e.target.value)}
                    placeholder="總結和最終結論"
                    rows="3"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                {/* Formulas Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-indigo-600">相關公式</label>
                    <button
                      onClick={addFormula}
                      className="text-sm bg-indigo-500 text-white px-3 py-1 rounded hover:bg-indigo-600 transition-colors"
                    >
                      + 添加公式
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(templateData.formulas || []).map((formula, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={formula}
                          onChange={(e) => updateFormula(index, e.target.value)}
                          placeholder="輸入LaTeX公式，例如：x^2 + y^2 = r^2"
                          className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors text-sm"
                        />
                        <button
                          onClick={() => removeFormula(index)}
                          className="text-red-500 hover:text-red-700 px-2 py-1 rounded"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {(!templateData.formulas || templateData.formulas.length === 0) && (
                      <p className="text-sm text-gray-500 italic">點擊「添加公式」來添加數學公式</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mt-8 bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">模板完成度</span>
            <span className="text-sm text-gray-600">
              {getCompletedFieldsCount()}/6 個欄位已完成
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${calculateCompletion()}%` }}
            ></div>
          </div>
        </div>

        {/* Formula Preview */}
        {templateData.formulas && templateData.formulas.length > 0 && (
          <div className="mt-8 bg-indigo-50 border border-indigo-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-indigo-900 mb-3">公式預覽</h3>
            <div className="space-y-3">
              {templateData.formulas.map((formula, index) => (
                formula.trim() && (
                  <div key={index} className="p-3 bg-white rounded border-l-4 border-indigo-400">
                    <div className="math-content text-center">{`$$${formula}$$`}</div>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {/* Useful Expressions */}
        {usefulExpressions && 
         templateData.type && 
         usefulExpressions[templateData.type] && 
         Array.isArray(usefulExpressions[templateData.type]) && 
         usefulExpressions[templateData.type].length > 0 && (
          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">
                  {templateTypes.find(t => t.id === templateData.type)?.name || templateData.type} 常用表達式
                </h3>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {usefulExpressions[templateData.type]
                    .filter(expression => expression && typeof expression === 'object' && expression.id && expression.text)
                    .map((expression) => (
                      <div 
                        key={expression.id} 
                        className="text-sm text-yellow-800 bg-yellow-100 p-3 rounded-lg border border-yellow-200 hover:bg-yellow-200 transition-colors cursor-pointer"
                        onClick={() => {
                          // Auto-suggest functionality - add to current field focus
                          const activeElement = document.activeElement;
                          if (activeElement && activeElement.tagName === 'TEXTAREA') {
                            const currentValue = activeElement.value;
                            const newValue = currentValue ? `${currentValue} ${expression.text}` : expression.text;
                            
                            // Determine field name based on placeholder or other attributes
                            let fieldName = '';
                            if (activeElement.placeholder.includes('問題')) fieldName = 'problem';
                            else if (activeElement.placeholder.includes('已知')) fieldName = 'given';
                            else if (activeElement.placeholder.includes('求證')) fieldName = 'toProve';
                            else if (activeElement.placeholder.includes('證明')) fieldName = 'proof';
                            else if (activeElement.placeholder.includes('結論')) fieldName = 'conclusion';
                            
                            if (fieldName) {
                              handleTemplateChange(fieldName, newValue);
                            }
                          }
                        }}
                      >
                        <div className="font-medium">{String(expression.text)}</div>
                        {expression.latex && (
                          <div className="math-content text-xs mt-1">{`$${String(expression.latex)}$`}</div>
                        )}
                      </div>
                    ))
                  }
                </div>
                <p className="text-xs text-yellow-700 mt-3">
                  💡 提示：點擊任何表達式將其添加到您當前的文字欄位中
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateWizard;
